/* One cart evaluation for every surface. Dates and reservation rules belong to the server. */
(function (host) {
  'use strict';
  const DATE = '配達希望日';
  const SLOT = '配達希望時間帯';
  const EVALUATION = 'plus2do_evaluation_id';
  const MAX_AGE = 60000;
  const TIMEOUT = 8000;
  const signature = (cart) => JSON.stringify((cart.items || []).map((item) => [
    item.key, String(item.variant_id || item.id), item.quantity, item.requires_shipping,
    item.selling_plan_allocation?.selling_plan?.id || null,
  ]));
  const choices = (values) => Array.isArray(values) ? values.filter((v) => v && typeof v.value === 'string' && typeof v.label === 'string') : [];
  function reservationSummaryLabel(data) {
    const date = data?.reservationStartDate;
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
    const instant = Date.parse(date + 'T00:00:00Z');
    if (!Number.isFinite(instant) || new Date(instant).toISOString().slice(0, 10) !== date) return '';
    return typeof data.reservationStartDateLabel === 'string' && data.reservationStartDateLabel.trim()
      ? data.reservationStartDateLabel.trim() : date;
  }
  function remainingLifetime(data, now) {
    const generated = Date.parse(data.generatedAt);
    const expires = Date.parse(data.validUntil);
    if (!Number.isFinite(generated) || !Number.isFinite(expires) || expires <= generated) return 0;
    // Never extend a server lease, including when the device clock is behind the server.
    return Math.max(0, Math.min(MAX_AGE, expires - generated, expires - now, generated + MAX_AGE - now));
  }
  function invalidSelection(data, attributes) {
    if (data.status !== 'ready') return {};
    const result = {};
    if (attributes[DATE] && !choices(data.dates).some((d) => d.value === attributes[DATE])) result[DATE] = '';
    if (attributes[SLOT] && !(data.timeSlots || []).includes(attributes[SLOT])) result[SLOT] = '';
    return result;
  }
  class ShippingScheduleController {
    constructor({ fetch, now = () => Date.now(), timers = host, root = '/', endpoint } = {}) {
      this.fetch = fetch;
      this.now = now;
      this.timers = timers;
      this.root = root;
      this.endpoint = endpoint || `${root}apps/plus2do-options/delivery-schedule`;
      this.listeners = new Set();
      this.generation = 0;
      this.selectionVersion = 0;
      this.mutations = 0;
      this.state = { status: 'loading', attributes: {}, saveStatus: '', cart: null, data: null };
      this.writeQueue = Promise.resolve();
      this.pendingWrites = 0;
      this.draft = null;
    }
    subscribe(listener) {
      this.listeners.add(listener);
      listener(this.state);
      return () => this.listeners.delete(listener);
    }
    emit(patch) {
      this.state = { ...this.state, ...patch };
      this.listeners.forEach((listener) => listener(this.state));
    }
    invalidate() {
      this.generation++;
      this.abort?.abort();
      this.timers.clearTimeout(this.expiryTimer);
      this.emit({ status: 'loading', data: null, saveStatus: this.state.saveStatus === 'saved' ? '' : this.state.saveStatus });
    }
    beginMutation() {
      this.mutations++;
      this.invalidate();
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        this.mutations--;
        this.scheduleRefresh();
      };
    }
    scheduleRefresh() {
      this.timers.clearTimeout(this.refreshTimer);
      if (this.mutations) return;
      this.refreshTimer = this.timers.setTimeout(() => this.refresh(), 30);
    }
    async json(url, options = {}) {
      const abort = new AbortController();
      const relay = () => abort.abort();
      options.signal?.addEventListener('abort', relay, { once: true });
      if (options.signal?.aborted) abort.abort();
      const timer = this.timers.setTimeout(() => abort.abort(), TIMEOUT);
      try {
        const response = await this.fetch(url, { ...options, cache: 'no-store', credentials: 'same-origin', signal: abort.signal,
          headers: { Accept: 'application/json', ...options.headers } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } finally {
        this.timers.clearTimeout(timer);
        options.signal?.removeEventListener('abort', relay);
      }
    }
    async refresh() {
      if (this.mutations) return;
      this.invalidate();
      const generation = this.generation;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      try {
        // add.js returns added lines, not the whole cart. Always obtain the canonical cart.
        const cart = await this.json(`${this.root}cart.js`, { signal });
        if (generation !== this.generation) return;
        if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
        const attributes = cart.attributes || {};
        this.emit({ cart, attributes });
        if (!cart.items.length || !cart.items.some((item) => item.requires_shipping)) {
          this.emit({ status: 'empty', data: null });
          this.arm(MAX_AGE);
          return;
        }
        if (cart.items.length > 100 || cart.items.some((item) => item.selling_plan_allocation)) {
          this.emit({ status: 'unsupported', data: null });
          this.arm(MAX_AGE);
          return;
        }
        const data = await this.json(this.endpoint, { method: 'POST', signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ schemaVersion: 1, calculationVersion: 'p2d-combined-v1', clientGeneration: generation, scope: 'shipping',
            lines: cart.items.map((item) => ({ variantId: String(item.variant_id || item.id), quantity: item.quantity })) }) });
        if (generation !== this.generation) return;
        if (data.clientGeneration !== undefined && data.clientGeneration !== generation) throw new Error('Mismatched generation');
        if (data.schemaVersion !== 1 || !['ready', 'unknown', 'unsupported'].includes(data.status)) throw new Error('Unsupported contract');
        const lifetime = remainingLifetime(data, this.now());
        if (!lifetime) throw new Error('Expired evaluation');
        if (data.status === 'ready' && (data.calculationVersion !== 'p2d-combined-v1' ||
          typeof data.reservationApplied !== 'boolean' || !data.cartFingerprint || !data.calendarRevision || !data.reservationRevision ||
          !/^\d{4}-\d{2}-\d{2}$/.test(data.shipDate || '') || !Array.isArray(data.dates) || !Array.isArray(data.timeSlots) ||
          data.dates.some((d) => !d || !/^\d{4}\/\d{2}\/\d{2}$/.test(d.value) || typeof d.label !== 'string') ||
          data.timeSlots.some((slot) => typeof slot !== 'string'))) {
          throw new Error('Incomplete or expired evaluation');
        }
        this.emit({ status: data.status, data, signature: signature(cart) });
        this.arm(lifetime || MAX_AGE);
        if (data.status === 'ready' && data.reservationApplied === true) {
          const patch = invalidSelection(data, attributes);
          if (typeof data.evaluationId === 'string' && data.evaluationId && data.evaluationId !== attributes[EVALUATION]) patch[EVALUATION] = data.evaluationId;
          if (Object.keys(patch).length) this.persist(patch, { generation, automatic: true,
            invalidated: DATE in patch || SLOT in patch, selectionVersion: this.selectionVersion });
        }
      } catch (error) {
        if (generation !== this.generation) return;
        this.emit({ status: 'unknown', data: null });
        this.arm(MAX_AGE);
      }
    }
    arm(delay) {
      this.timers.clearTimeout(this.expiryTimer);
      this.expiryTimer = this.timers.setTimeout(() => { this.invalidate(); this.scheduleRefresh(); }, Math.max(1, delay));
    }
    select(date, slot) {
      if (this.state.status !== 'ready' || !remainingLifetime(this.state.data, this.now())) {
        this.invalidate(); this.scheduleRefresh(); return Promise.resolve();
      }
      const patch = { [DATE]: date, [SLOT]: slot };
      if (Object.keys(invalidSelection(this.state.data, patch)).length) return Promise.resolve();
      this.selectionVersion++;
      this.draft = patch;
      return this.persist(patch, { generation: this.generation, selectionVersion: this.selectionVersion });
    }
    persist(patch, options) {
      this.pendingWrites++;
      const work = async () => {
        if (options.generation !== this.generation || options.selectionVersion !== this.selectionVersion) return;
        const preferencesChanged = DATE in patch || SLOT in patch;
        if (preferencesChanged) this.emit({ saveStatus: 'saving' });
        try {
          const cart = await this.json(`${this.root}cart/update.js`, { method: 'POST',
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: patch }) });
          if (cart.errors || !cart.attributes || Object.keys(patch).some((key) => (cart.attributes[key] || '') !== patch[key])) {
            throw new Error('Attribute write not confirmed');
          }
          if (options.selectionVersion !== this.selectionVersion) return;
          if (options.generation !== this.generation) { this.scheduleRefresh(); return; }
          this.draft = null;
          this.emit({ attributes: cart.attributes, saveStatus: options.invalidated ? 'invalidated' : options.automatic ? this.state.saveStatus : 'saved' });
          if (signature(cart) !== this.state.signature) this.scheduleRefresh();
        } catch (error) {
          if (options.selectionVersion === this.selectionVersion) {
            this.emit({ saveStatus: preferencesChanged ? 'error' : 'evaluationError' });
          }
        }
      };
      this.writeQueue = this.writeQueue.then(work, work).finally(() => { this.pendingWrites--; });
      return this.writeQueue;
    }
    retry() {
      if (this.draft && this.state.status === 'ready' && remainingLifetime(this.state.data, this.now())) {
        return this.select(this.draft[DATE], this.draft[SLOT]);
      }
      return this.refresh();
    }
    async flush() {
      // Customer can always continue (R5). A slow network never blocks checkout indefinitely.
      let timer;
      await Promise.race([this.writeQueue, new Promise((resolve) => { timer = this.timers.setTimeout(resolve, 750); })]);
      this.timers.clearTimeout(timer);
    }
    destroy() {
      this.abort?.abort();
      this.timers.clearTimeout(this.expiryTimer);
      this.timers.clearTimeout(this.refreshTimer);
      this.listeners.clear();
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { ShippingScheduleController, signature, remainingLifetime, invalidSelection, reservationSummaryLabel };
  if (!host.document) return;
  const configElement = host.document.getElementById('Plus2doShippingConfig');
  if (!configElement) return;
  const config = JSON.parse(configElement.textContent);
  if (config.enabled !== true) return;
  const store = host.Plus2doShipping = new ShippingScheduleController({ fetch: (...args) => host.fetch(...args), root: config.root });
  store.messages = config.messages;
  const document = host.document;
  const message = (key) => config.messages[key] || '';
  const format = (key, date) => message(key).replace('[date]', date || '');
  class ShippingScheduleSummary extends HTMLElement {
    connectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = store.subscribe((state) => this.render(state));
      this.retry = () => store.retry();
      this.querySelector('[data-shipping-retry]')?.addEventListener('click', this.retry);
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.querySelector('[data-shipping-retry]')?.removeEventListener('click', this.retry);
    }
    render(state) {
      const line = this.dataset.lineKey;
      const displayDate = reservationSummaryLabel(state.data);
      const activeReservation = state.status === 'ready' && state.data?.reservationApplied === true && !!displayDate;
      this.hidden = !activeReservation || state.status === 'empty' || (line && state.cart && !state.cart.items.some((item) => item.key === line && item.requires_shipping));
      this.setAttribute('aria-busy', String(state.status === 'loading'));
      const ready = state.status === 'ready';
      const data = state.data;
      const text = activeReservation ? format('combined', displayDate) : '';
      this.querySelector('[data-shipping-date]').textContent = text;
      const detail = this.querySelector('[data-shipping-detail]');
      if (detail) { detail.textContent = ready && data.reservationApplied ? message('combinedDetail') : ''; detail.hidden = !detail.textContent; }
      const status = this.querySelector('[data-shipping-save]');
      if (status) { status.textContent = message(state.saveStatus); status.hidden = !status.textContent; }
      const retry = this.querySelector('[data-shipping-retry]');
      if (retry) retry.hidden = !(state.status === 'unknown' || state.status === 'unsupported' || ['error', 'evaluationError'].includes(state.saveStatus));
    }
  }
  if (!customElements.get('shipping-schedule-summary')) customElements.define('shipping-schedule-summary', ShippingScheduleSummary);
  // One live region, not one announcement per line/surface.
  store.subscribe((state) => {
    const live = document.getElementById('ShippingScheduleLive');
    if (live) live.textContent = state.status === 'ready' && state.data?.reservationApplied === true && reservationSummaryLabel(state.data) ? message(['error', 'evaluationError'].includes(state.saveStatus) ? state.saveStatus : state.status === 'ready' ? 'updated' : state.status === 'loading' ? 'loading' : 'unknown') : '';
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, () => store.scheduleRefresh());
  ['cart:updated', 'cart:refresh'].forEach((name) => document.addEventListener(name, () => { store.invalidate(); store.scheduleRefresh(); }));
  host.addEventListener('pageshow', () => { store.invalidate(); store.scheduleRefresh(); });
  document.addEventListener('visibilitychange', () => {
    store.invalidate();
    if (document.visibilityState === 'visible') store.scheduleRefresh();
  });
  const resubmitting = new WeakSet();
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (event.defaultPrevented || event.submitter?.name !== 'checkout' || resubmitting.has(form)) return;
    // Do not change disabled/validation state owned by Shopify or another app.
    if (!store.pendingWrites) return;
    event.preventDefault();
    await store.flush();
    if (!form.isConnected) return;
    resubmitting.add(form);
    try { form.requestSubmit(event.submitter?.isConnected ? event.submitter : undefined); }
    finally { resubmitting.delete(form); }
  });
  store.refresh();
})(typeof window === 'undefined' ? globalThis : window);
